import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, MapPin, Timer, Gauge, Star } from 'lucide-react';
import { useDiveLogs } from '@/data/dive-logs';
import { StaticMap } from '@/components/map/StaticMap';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { formatGermanDate } from '@/lib/format';
import { useDocumentTitle } from '@/lib/useDocumentTitle';

export function DiveLogsListPage() {
  useDocumentTitle('Tauchgänge');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useDiveLogs({ page, limit: 24 });

  const items      = data?.items ?? [];
  const total      = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 24));

  return (
    <div className="space-y-6 p-4 sm:p-6 md:p-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Tauchgänge</h1>
          <p className="text-sm text-[var(--muted-foreground)]">{total} Einträge insgesamt.</p>
        </div>
        <Link
          to="/logs/new"
          className="inline-flex h-10 items-center gap-2 rounded-md bg-ocean-700 px-4 text-sm font-medium text-white shadow-sm hover:bg-ocean-600"
        >
          <Plus className="h-4 w-4" /> Neuer Tauchgang
        </Link>
      </header>

      {isLoading ? (
        <div className="grid place-items-center py-20"><Spinner className="h-8 w-8" /></div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-[var(--muted-foreground)]">
            Du hast noch keine Tauchgänge geloggt.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((log) => {
            const hasCoords = log.diveSite?.geoLatitude != null && log.diveSite?.geoLongitude != null;
            return (
              <Link
                key={log.id}
                to="/logs/$id"
                params={{ id: String(log.id) }}
                className="group block"
              >
                <Card className="h-full overflow-hidden border-transparent transition group-hover:shadow-xl group-hover:ring-2 group-hover:ring-ocean-200">
                  <div className="relative h-32 w-full bg-gradient-to-br from-ocean-700 to-ocean-900">
                    {hasCoords && (
                      <StaticMap
                        lat={log.diveSite!.geoLatitude!}
                        lng={log.diveSite!.geoLongitude!}
                        zoom={11}
                        width={480}
                        height={140}
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-ocean-900/80 via-ocean-900/20 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3">
                      <h2 className="line-clamp-2 text-base font-semibold leading-tight text-white">
                        {log.title}
                      </h2>
                      {log.userIndex && (
                        <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur">
                          #{log.userIndex}
                        </span>
                      )}
                    </div>
                  </div>
                  <CardContent className="space-y-3 pt-4">
                    <p className="text-xs text-[var(--muted-foreground)]">{formatGermanDate(log.diveDate)}</p>
                    {log.diveSite && (
                      <p className="flex items-center gap-1 truncate text-sm">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-ocean-700" />
                        <span className="truncate">{log.diveSite.label}</span>
                      </p>
                    )}
                    <div className="grid grid-cols-3 gap-1 border-t border-[var(--border)] pt-3 text-center text-xs">
                      <div>
                        <p className="flex items-center justify-center gap-1 font-semibold text-base">
                          <Gauge className="h-3.5 w-3.5 text-ocean-700" />
                          {log.maxDepth}
                        </p>
                        <p className="text-[var(--muted-foreground)]">Meter</p>
                      </div>
                      <div>
                        <p className="flex items-center justify-center gap-1 font-semibold text-base">
                          <Timer className="h-3.5 w-3.5 text-ocean-700" />
                          {log.diveTimeMinutes}
                        </p>
                        <p className="text-[var(--muted-foreground)]">Minuten</p>
                      </div>
                      <div>
                        <p className="flex items-center justify-center gap-0.5 font-semibold text-base">
                          {log.rating ? (
                            <>
                              <Star className="h-3.5 w-3.5 fill-coral-500 text-coral-500" />
                              {log.rating}
                            </>
                          ) : (
                            '–'
                          )}
                        </p>
                        <p className="text-[var(--muted-foreground)]">Rating</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-4">
          <button
            type="button"
            className="rounded-md border border-[var(--border)] px-4 py-1.5 text-sm transition hover:bg-[var(--muted)] disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹ Zurück
          </button>
          <span className="text-sm text-[var(--muted-foreground)]">Seite {page} / {totalPages}</span>
          <button
            type="button"
            className="rounded-md border border-[var(--border)] px-4 py-1.5 text-sm transition hover:bg-[var(--muted)] disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Weiter ›
          </button>
        </div>
      )}
    </div>
  );
}
